<div class="box">
  <div class="box-row header mb-2">
    <app-header-bar
      [backRoute]="'/organizations'"
      [title]="organization?.name || 'organizations.title'"
      [autoTranslateTitle]="organization === undefined"
    ></app-header-bar>
  </div>

  <app-loading-indicator [loading]="isLoading"></app-loading-indicator>

  @if (organization) {
    <div class="card mb-3">
      <div class="card-body">
        <div
          class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3"
        >
          <div>
            <h5 class="card-title mb-1">
              {{ 'organizations.details' | translate }}
            </h5>
            <p class="text-muted mb-0">
              {{ 'organizations.overviewSubtitle' | translate }}
            </p>
          </div>
          <div
            class="d-flex flex-wrap align-items-center justify-content-end gap-2"
          >
            <button
              type="button"
              class="btn btn-sm d-inline-flex align-items-center gap-2"
              [class.btn-outline-success]="organizationActive"
              [class.btn-outline-warning]="!organizationActive"
              [disabled]="actionLoading || editing"
              (click)="toggleOrganizationStatus()"
              [attr.title]="statusActionLabel | translate"
              [attr.aria-pressed]="organizationActive"
            >
              <span class="material-symbols-outlined" aria-hidden="true">{{
                organizationActive ? 'toggle_on' : 'toggle_off'
              }}</span>
              <span>{{ organization.status || '' | translate }}</span>
            </button>
            @if (!editing) {
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                [disabled]="actionLoading"
                (click)="editOrganization(form)"
                [attr.title]="'edit' | translate"
                [attr.aria-label]="'edit' | translate"
              >
                <span class="material-symbols-outlined">edit</span>
                <span class="d-none d-lg-inline">{{ 'edit' | translate }}</span>
              </button>
              <button
                type="button"
                class="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-2"
                [disabled]="actionLoading"
                (click)="deleteOrganization()"
                [attr.title]="'delete' | translate"
                [attr.aria-label]="'delete' | translate"
              >
                <span class="material-symbols-outlined">delete</span>
                <span class="d-none d-lg-inline">{{
                  'delete' | translate
                }}</span>
              </button>
            }
          </div>
        </div>

        @if (!editing) {
          <div class="row g-3">
            <div class="col-12 col-md-6 col-xl-3">
              <small class="text-muted text-uppercase">{{
                'organizations.name' | translate
              }}</small>
              <div class="fw-semibold">{{ organization.name || '-' }}</div>
            </div>
            <div class="col-12 col-md-6 col-xl-3">
              <small class="text-muted text-uppercase">{{
                'organizations.type' | translate
              }}</small>
              <div class="fw-semibold">
                {{ organization.type | organizationType }}
              </div>
            </div>
            <div class="col-12 col-md-6 col-xl-3">
              <small class="text-muted text-uppercase">{{
                'organizations.owner' | translate
              }}</small>
              <div class="fw-semibold">{{ ownerName }}</div>
            </div>
            <div class="col-12 col-md-6 col-xl-3">
              <small class="text-muted text-uppercase">ID</small>
              <div class="fw-semibold text-break">{{ organization.id }}</div>
            </div>
            <div class="col-12 col-md-6 col-xl-3">
              <small class="text-muted text-uppercase">{{
                'organizations.address' | translate
              }}</small>
              <div class="fw-semibold">{{ organization.address || '-' }}</div>
            </div>
            <div class="col-12 col-md-6 col-xl-3">
              <small class="text-muted text-uppercase">{{
                'organizations.parentOrganization' | translate
              }}</small>
              <div class="fw-semibold">
                {{ organization.parentOrganization?.name || '-' }}
              </div>
            </div>
            <div class="col-12 col-md-6 col-xl-3">
              <small class="text-muted text-uppercase">{{
                'organizations.motherOrganization' | translate
              }}</small>
              <div class="fw-semibold">
                {{ organization.motherOrganization?.name || '-' }}
              </div>
            </div>
            <div class="col-12 col-md-6 col-xl-3">
              <small class="text-muted text-uppercase">{{
                'createdAt' | translate
              }}</small>
              <div class="fw-semibold">
                {{ (organization.createdAt | dateTime) || '-' }}
              </div>
            </div>
            <div class="col-12">
              <small class="text-muted text-uppercase">{{
                'organizations.description' | translate
              }}</small>
              <div class="fw-semibold">
                {{ organization.description || '-' }}
              </div>
            </div>
          </div>
        }

        <app-organization-form
          #form
          [class.d-none]="!editing"
          (closeEvent)="onFormClose(form)"
          (loadingEvent)="onLoading($event.valueOf())"
        ></app-organization-form>

        @if (editing) {
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button
              type="button"
              class="btn btn-outline-primary"
              [disabled]="actionLoading"
              (click)="cancelEdit(form)"
            >
              {{ 'cancel' | translate }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              [disabled]="actionLoading || form.form.invalid"
              (click)="onSave(form)"
            >
              {{ 'save' | translate }}
            </button>
          </div>
        }
      </div>
    </div>

    <app-organization-license-usage-report
      [organizationId]="organization.id"
      (configureEntitlements)="
        editLicenseEntitlements(LicenseModal, licenseEntitlementsForm)
      "
    ></app-organization-license-usage-report>

    <div class="accordion mb-3" id="organization-detail-accordion">
      <div class="accordion-item">
        <h2
          class="accordion-header d-flex align-items-center gap-2 px-3 py-2"
          id="organization-users-heading"
          (click)="toggleUsers()"
          style="cursor: pointer"
        >
          <span class="fs-6 fw-normal flex-grow-1">
            {{ 'user.title' | translate }}
          </span>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2 flex-shrink-0"
            (click)="$event.stopPropagation(); organizationUsers.new()"
            [attr.aria-label]="'user.new' | translate"
          >
            <span class="material-symbols-outlined">person_add</span>
            <span class="d-none d-sm-inline">{{ 'user.new' | translate }}</span>
          </button>
          <button
            type="button"
            class="btn btn-link p-1 text-decoration-none d-inline-flex align-items-center justify-content-center flex-shrink-0"
            [attr.aria-expanded]="usersExpanded()"
            [attr.aria-label]="'user.title' | translate"
            aria-controls="organization-users-body"
          >
            <span class="material-symbols-outlined">
              {{
                usersExpanded() ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
              }}
            </span>
          </button>
        </h2>
        <div
          id="organization-users-body"
          class="accordion-collapse collapse"
          [class.show]="usersExpanded()"
          aria-labelledby="organization-users-heading"
        >
          <div class="accordion-body">
            <app-organization-user
              #organizationUsers
              [showNewAction]="false"
            ></app-organization-user>
          </div>
        </div>
      </div>
    </div>

    <app-model-modal #LicenseModal>
      <app-organization-license-entitlements-form
        #licenseEntitlementsForm
        (closeEvent)="LicenseModal.hide()"
        (loadingEvent)="LicenseModal.loading = $event.valueOf()"
      ></app-organization-license-entitlements-form>
    </app-model-modal>
  }

  <app-error [error]="error"></app-error>
</div>
